// EXTERNAL DEPENDENCIES
import React, { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'

// HELPERS
import { scrollToTop } from '@/utils'
import { formatRawProfileDetails } from '../../utils'

// API
import { useProfileDetails } from '@/api/profile'

// REDUX
import {
  useBusinessProfilePageContent,
  useBusinessProfileReduxState,
} from '../../hooks'
import {
  updateEditedProfileDataValues,
  updateIsEditing,
  updateUneditedFormattedData,
} from '@/redux/features'

// COMPONENTS
import { Alert, Container } from '@paypalcorp/pp-react'
import PanelSliderWithProgressSteppers from '@/common/components/ui/panel-slider-with-progress-steppers'
import CenteredLoadingSpinner from '@/common/components/loading/centered-loading-spinner'
import { EditProfilePanel, ReviewProfilePanel } from './components'
import { PageHeaderSection } from '../page-header-section'

// FPTI
import { useImpressionTracking } from '@/hooks'
import { EDIT_PROFILE_SCREEN_SHOWN_FPTI } from './fpti'

// TEST IDS
import { EDIT_PROFILE_PAGE_TEST_ID } from '../../constants/test-ids'

// STYLES
import styles from './styles.module.less'

/**
 * A user who has already created a Business Profile can edit it via this page
 */
export function EditProfilePage() {
  useImpressionTracking(EDIT_PROFILE_SCREEN_SHOWN_FPTI)

  const dispatch = useDispatch()
  const getContent = useBusinessProfilePageContent()

  // NETWORK REQUESTS
  const { data: profileDetails, isFetching } = useProfileDetails()
  const { unformattedBusinessPhoneNumber, ...relevantContactInfo } =
    profileDetails?.contactInfo || {}

  // STATE
  const {
    profileData,
    editedProfileData: {
      currentPanelIndex,
      editPageIsLoading,
      errorMessage,
      values,
    },
    uneditedFormattedData,
  } = useBusinessProfileReduxState()

  // EFFECTS
  useEffect(() => {
    if (
      profileDetails &&
      profileData &&
      Object.keys(relevantContactInfo).length &&
      !values.businessName
    ) {
      const formattedProfileDetails = formatRawProfileDetails(
        profileDetails,
        profileData,
      )

      dispatch(updateUneditedFormattedData(formattedProfileDetails))
      dispatch(updateEditedProfileDataValues(formattedProfileDetails))
    }
  }, [
    dispatch,
    profileDetails,
    profileData,
    relevantContactInfo,
    values.businessName,
  ])

  useEffect(() => {
    scrollToTop()
  }, [currentPanelIndex])

  const resetEditedData = useCallback(() => {
    dispatch(updateEditedProfileDataValues(uneditedFormattedData))
  }, [uneditedFormattedData, dispatch])

  // PANELS
  const panels = [
    {
      panel: (
        <EditProfilePanel
          isLoading={isFetching}
          resetEditedData={resetEditedData}
        />
      ),
    },
    {
      panel: <ReviewProfilePanel />,
    },
  ]

  return (
    <Container data-testid={EDIT_PROFILE_PAGE_TEST_ID}>
      {editPageIsLoading ? (
        <CenteredLoadingSpinner height={'60vh'} />
      ) : (
        <>
          <PageHeaderSection
            handleBackClick={() => {
              dispatch(updateIsEditing(false))
              resetEditedData()
            }}
            pageHeaderText={getContent('editProfilePageHeader')}
          />
          {errorMessage && (
            <Alert type='error' className={styles.errorMessageContainer}>
              {errorMessage}
            </Alert>
          )}
          <PanelSliderWithProgressSteppers
            currentPanelIndex={currentPanelIndex}
            panels={panels}
          />
        </>
      )}
    </Container>
  )
}
